import { useForm } from "@tanstack/react-form";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  createPreviewActions,
  ShareablePreview,
} from "@/core/components/shared/preview-action";
import { Button } from "@/core/components/ui/button";
import { ButtonSpinner } from "@/core/components/ui/buttonStyled/ButtonSpinner.component";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/core/components/ui/dialog";
import { Input } from "@/core/components/ui/input";
import { Label } from "@/core/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/core/components/ui/select";
import { DEFAULT_CURRENCY, majorToMinor } from "@/core/utils/func/money-minor";
import { notifSilence, notifSilenceError } from "@/core/utils/func/notifApp";
import { useCreatePromotion } from "@/features/fee-engine/api/use-create-promotion";
import type { PromotionDto } from "@/features/fee-engine/api/dto";
import { useFeeEngineTranslation } from "@/features/fee-engine/i18n/use-fee-engine-translation";
import {
  createPromotionSchema,
  type PromotionFormValues,
} from "@/features/fee-engine/schemas/promotion.schema";
import {
  DISCOUNT_TYPES,
  PROMOTION_STATUSES,
} from "@/features/shared/api/enums";

const EMPTY_VALUES: PromotionFormValues = {
  code: "",
  description: "",
  discountType: "PERCENT_OFF_FEE",
  value: 0,
  productCode: "",
  usageLimitTotal: undefined,
  usageLimitPerParty: undefined,
  validFrom: "",
  validTo: "",
  status: "DRAFT",
};

const toPayload = (values: PromotionFormValues): PromotionDto => {
  // `value` : % pour PERCENT_OFF_FEE, montant en unité MAJEURE → mineure pour
  // FIXED_OFF_FEE, ignoré pour FEE_WAIVED (envoyé à 0).
  let payloadValue = values.value;
  if (values.discountType === "FIXED_OFF_FEE") {
    payloadValue = majorToMinor(values.value, DEFAULT_CURRENCY);
  } else if (values.discountType === "FEE_WAIVED") {
    payloadValue = 0;
  }
  return {
    code: values.code.trim().toUpperCase(),
    description: values.description?.trim() || null,
    discountType: values.discountType,
    value: payloadValue,
    productCode: values.productCode?.trim() || null,
    usageLimitTotal: values.usageLimitTotal ?? null,
    usageLimitPerParty: values.usageLimitPerParty ?? null,
    validFrom: values.validFrom,
    validTo: values.validTo?.trim() || null,
    status: values.status,
  };
};

/** Formulaire de création d'une promotion (`POST /Promotions/add`, §6.5). */
export function PromotionFormDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useFeeEngineTranslation();
  const { i18n } = useTranslation();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- i18n.language : reconstruit le schéma zod traduit à chaque changement de langue.
  const schema = useMemo(() => createPromotionSchema(), [i18n.language]);

  const create = useCreatePromotion();
  const [preview, setPreview] = useState<PromotionDto | null>(null);

  const form = useForm({
    defaultValues: EMPTY_VALUES,
    onSubmit: async ({ value }) => {
      const parsed = schema.safeParse(value);
      if (!parsed.success) return;
      setPreview(toPayload(parsed.data));
    },
  });

  const handleConfirm = () => {
    if (!preview) return;
    create.mutate(preview, {
      onSuccess: () => {
        notifSilence(t("fees.promotions.feedback.created"));
        setPreview(null);
        onOpenChange(false);
      },
      onError: (error) => notifSilenceError(error.message),
    });
  };

  const fieldError =
    (name: keyof PromotionFormValues) => (props: { value: unknown }) => {
      const shape = schema.shape[name];
      const result = shape.safeParse(props.value ?? "");
      return result.success ? undefined : result.error.issues[0]?.message;
    };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t("fees.promotions.create")}</DialogTitle>
          </DialogHeader>
          <form
            className="grid grid-cols-1 gap-4 sm:grid-cols-2"
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              void form.handleSubmit();
            }}
          >
            <form.Field name="code" validators={{ onChange: fieldError("code") }}>
              {(field) => (
                <div className="space-y-1.5">
                  <Label htmlFor="code">{t("fees.promotions.form.code")}</Label>
                  <Input
                    id="code"
                    className="uppercase notranslate"
                    placeholder={t("fees.promotions.form.codePlaceholder")}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) =>
                      field.handleChange(e.target.value.toUpperCase())
                    }
                  />
                  {field.state.meta.errors.length > 0 && (
                    <p className="text-sm text-destructive">
                      {field.state.meta.errors[0]}
                    </p>
                  )}
                </div>
              )}
            </form.Field>

            <form.Field name="discountType">
              {(field) => (
                <div className="space-y-1.5">
                  <Label>{t("fees.promotions.form.discountType")}</Label>
                  <Select
                    value={field.state.value}
                    onValueChange={(v) =>
                      field.handleChange(v as PromotionFormValues["discountType"])
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DISCOUNT_TYPES.map((v) => (
                        <SelectItem key={v} value={v} className="notranslate">
                          {v}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </form.Field>

            <form.Field
              name="description"
              validators={{ onChange: fieldError("description") }}
            >
              {(field) => (
                <div className="space-y-1.5 sm:col-span-2">
                  <Label htmlFor="description">
                    {t("fees.promotions.form.description")}
                  </Label>
                  <Input
                    id="description"
                    placeholder={t("fees.promotions.form.descriptionPlaceholder")}
                    value={field.state.value ?? ""}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                </div>
              )}
            </form.Field>

            <form.Field
              name="value"
              validators={{ onChange: fieldError("value") }}
            >
              {(field) => (
                <div className="space-y-1.5">
                  <Label htmlFor="value">
                    {t("fees.promotions.form.value")}
                  </Label>
                  <Input
                    id="value"
                    type="number"
                    step="0.01"
                    min={0}
                    className="notranslate"
                    placeholder={t("fees.promotions.form.valuePlaceholder")}
                    value={field.state.value ?? ""}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.valueAsNumber)}
                  />
                  {field.state.meta.errors.length > 0 && (
                    <p className="text-sm text-destructive">
                      {field.state.meta.errors[0]}
                    </p>
                  )}
                </div>
              )}
            </form.Field>

            <form.Field name="productCode">
              {(field) => (
                <div className="space-y-1.5">
                  <Label htmlFor="productCode">
                    {t("fees.promotions.form.productCode")}
                  </Label>
                  <Input
                    id="productCode"
                    className="notranslate"
                    placeholder={t("fees.promotions.form.productCodePlaceholder")}
                    value={field.state.value ?? ""}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                </div>
              )}
            </form.Field>

            <form.Field name="usageLimitTotal">
              {(field) => (
                <div className="space-y-1.5">
                  <Label htmlFor="usageLimitTotal">
                    {t("fees.promotions.form.usageLimitTotal")}
                  </Label>
                  <Input
                    id="usageLimitTotal"
                    type="number"
                    min={0}
                    className="notranslate"
                    placeholder={t(
                      "fees.promotions.form.usageLimitTotalPlaceholder"
                    )}
                    value={field.state.value ?? ""}
                    onBlur={field.handleBlur}
                    onChange={(e) =>
                      field.handleChange(
                        Number.isFinite(e.target.valueAsNumber)
                          ? e.target.valueAsNumber
                          : undefined
                      )
                    }
                  />
                </div>
              )}
            </form.Field>

            <form.Field name="usageLimitPerParty">
              {(field) => (
                <div className="space-y-1.5">
                  <Label htmlFor="usageLimitPerParty">
                    {t("fees.promotions.form.usageLimitPerParty")}
                  </Label>
                  <Input
                    id="usageLimitPerParty"
                    type="number"
                    min={0}
                    className="notranslate"
                    placeholder={t(
                      "fees.promotions.form.usageLimitPerPartyPlaceholder"
                    )}
                    value={field.state.value ?? ""}
                    onBlur={field.handleBlur}
                    onChange={(e) =>
                      field.handleChange(
                        Number.isFinite(e.target.valueAsNumber)
                          ? e.target.valueAsNumber
                          : undefined
                      )
                    }
                  />
                </div>
              )}
            </form.Field>

            <form.Field
              name="validFrom"
              validators={{ onChange: fieldError("validFrom") }}
            >
              {(field) => (
                <div className="space-y-1.5">
                  <Label htmlFor="validFrom">
                    {t("fees.promotions.form.validFrom")}
                  </Label>
                  <Input
                    id="validFrom"
                    type="date"
                    className="notranslate"
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                  {field.state.meta.errors.length > 0 && (
                    <p className="text-sm text-destructive">
                      {field.state.meta.errors[0]}
                    </p>
                  )}
                </div>
              )}
            </form.Field>

            <form.Field name="validTo">
              {(field) => (
                <div className="space-y-1.5">
                  <Label htmlFor="validTo">
                    {t("fees.promotions.form.validTo")}
                  </Label>
                  <Input
                    id="validTo"
                    type="date"
                    className="notranslate"
                    value={field.state.value ?? ""}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                </div>
              )}
            </form.Field>

            <form.Field name="status">
              {(field) => (
                <div className="space-y-1.5">
                  <Label>{t("fees.promotions.form.status")}</Label>
                  <Select
                    value={field.state.value}
                    onValueChange={(v) =>
                      field.handleChange(v as PromotionFormValues["status"])
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PROMOTION_STATUSES.map((v) => (
                        <SelectItem key={v} value={v} className="notranslate">
                          {v}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </form.Field>

            <DialogFooter className="sm:col-span-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={create.isPending}
              >
                {t("common.cancel")}
              </Button>
              <Button type="submit" disabled={create.isPending}>
                {create.isPending ? <ButtonSpinner /> : null}
                {t("common.confirm")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {preview ? (
        <ShareablePreview
          data={preview}
          title={t("fees.promotions.preview.createTitle")}
          description={t("fees.promotions.preview.createDescription")}
          mode="user"
          // Section frais indicative : la remise étant un pourcentage OU un
          // montant selon le type, on affiche la « valeur » brute et le
          // total (impact réel calculé plus tard par le devis fee-engine).
          fees={[]}
          total={{
            label: t("fees.promotions.preview.valueLabel"),
            amount: preview.value,
            currency: DEFAULT_CURRENCY,
          }}
          defaultOpen
          onOpenChange={(isOpen) => {
            if (!isOpen && !create.isPending) setPreview(null);
          }}
          maxHeight="90vh"
          actions={createPreviewActions({
            onConfirm: handleConfirm,
            onCancel: () => setPreview(null),
            t,
            confirmLoading: create.isPending,
            withIndicatorOnConfirm: true,
          })}
        />
      ) : null}
    </>
  );
}
